/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, March 12th 2022, 9:55:52 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  TaskTemplate,
  TaskTemplateValidationSchema,
  DigitalSigningSettings,
  DigitalSigningSettingsValidationSchema,
  IUserTaskTypesData,
} from "lib/schemas";
import { APISERVICEPATHS, useAPIMutation, useAPIForm, useAPIRequest } from "lib/api";
import { PrimaryKeyValue } from "lib/types";
import { useWorkflowStore } from "service-builder/stores";
import { useDeleteOSItem } from "version-control/services/DeleteOSItemAPI";

const taskDefinitionsAPIURL = APISERVICEPATHS.taskDefinitions;

export const useAPIMutateTaskTemplate = () => useAPIMutation<TaskTemplate>({ apiServicePath: taskDefinitionsAPIURL });

export const useTaskTemplateAPIForm = (
  taskTemplateFetchId: PrimaryKeyValue<TaskTemplate>,
  taskTemplate: TaskTemplate,
  updateTaskTemplateFromAPI: (object: TaskTemplate) => void
) => {
  const useAPIFormReturn = useAPIForm({
    apiFormDefaultValues: taskTemplate,
    apiFormDataValidationSchema: TaskTemplateValidationSchema,
    apiInitialFetchId: taskTemplateFetchId,
    apiServicePath: taskDefinitionsAPIURL,
    setAPIFormResponse: updateTaskTemplateFromAPI,
    primaryKey: "id",
    formResetDependencyArray: [taskTemplate?.sequence, taskTemplate?.next],
  });

  return useAPIFormReturn;
};

export const useDeleteTaskTemplate = () => useDeleteOSItem(taskDefinitionsAPIURL);

const taskDefinitionsReorderAPIURL = APISERVICEPATHS.taskDefinitionsReorder;

export const useAPIReorderTaskTemplate = () =>
  useAPIMutation<TaskTemplate>({ apiServicePath: taskDefinitionsReorderAPIURL });

const digitalSigningSettingsAPIPath = APISERVICEPATHS.digitalSigningSettings;
export const useDigitalSigninSettingsAPI = () => {
  return {
    useList: () =>
      useAPIRequest<DigitalSigningSettings[]>({
        apiServicePath: digitalSigningSettingsAPIPath,
        makeRequestOnInitialMount: false,
      }),
    useMutate: () =>
      useAPIMutation<DigitalSigningSettings>({
        apiServicePath: digitalSigningSettingsAPIPath,
      }),
    useAPIForm: (args: {
      id: DigitalSigningSettings["id"];
      defaultValues?: DigitalSigningSettings;
      onAPISave: (a: DigitalSigningSettings) => void;
    }) => {
      return useAPIForm({
        apiServicePath: digitalSigningSettingsAPIPath,
        apiFormDataValidationSchema: DigitalSigningSettingsValidationSchema,
        apiFormDefaultValues: args?.defaultValues,
        apiInitialFetchId: args?.id,
        primaryKey: "id",
        setAPIFormResponse: args?.onAPISave,
        method: args?.id ? "PATCH" : "POST",
      });
    },
    useDelete: () => useDeleteOSItem<DigitalSigningSettings>(digitalSigningSettingsAPIPath),
  };
};

export const useAPIGetUserTaskTypes = () => {
  const { makeRequest, ...rest } = useAPIRequest<IUserTaskTypesData>({
    apiServicePath: APISERVICEPATHS.userTaskTypes,
    makeRequestOnInitialMount: false,
  });

  const { userTaskTypes, setUserTaskTypes } = useWorkflowStore();

  const loadUserTaskTypesFromAPI = React.useCallback(() => {
    if (Object.keys(userTaskTypes).length) return;
    makeRequest({ setValidAPIResponse: setUserTaskTypes });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(userTaskTypes)]);

  return { loadUserTaskTypesFromAPI, ...rest };
};
